import { useState } from "react";

import { useAccommodationLookups } from "@/hooks/queries/lookups/useAccommodationLookups";
import { usePropertyLookups } from "@/hooks/queries/lookups/usePropertyLookups";
import { useLocationLookups } from "@/hooks/queries/lookups/useLocationLookups";
import { useVerificationLookups } from "@/hooks/queries/lookups/useVerificationLookups";
import { useSubscriptionLookups } from "@/hooks/queries/lookups/useSubscriptionLookups";
import { useGeneralLookups } from "@/hooks/queries/lookups/useGeneralLookups";
import useRegisterableRoles from "@/hooks/queries/useRegisterableRoles";

type LookupDomain =
  | "accommodation"
  | "property"
  | "location"
  | "verification"
  | "general"
  | "subscriptions"
  | "roles";

export default function LookupTester() {
  const [selected, setSelected] = useState<LookupDomain>("accommodation");

  const accommodation = useAccommodationLookups();
  const property = usePropertyLookups();
  const location = useLocationLookups();
  const verification = useVerificationLookups();
  const general = useGeneralLookups();
  const subscriptions = useSubscriptionLookups();
  const roles = useRegisterableRoles();

  const current = {
    accommodation,
    property,
    location,
    verification,
    general,
    subscriptions,
    roles,
  }[selected];

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-8">
      <h1 className="text-3xl font-bold">Lookup Playground 🧪</h1>

      <div className="rounded-lg border p-4">
        <label className="mb-2 block font-medium">Select Lookup Endpoint</label>

        <select
          value={selected}
          onChange={(e) => setSelected(e.target.value as LookupDomain)}
          className="w-full rounded border px-3 py-2"
        >
          <option value="accommodation">Accommodation</option>

          <option value="property">Property</option>

          <option value="location">Location</option>

          <option value="verification">Verification</option>

          <option value="general">General</option>

          <option value="subscriptions">Subscriptions</option>

          <option value="roles">Roles (Admin)</option>
        </select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <InfoCard title="Loading" value={String(current.isPending)} />

        <InfoCard title="Fetching" value={String(current.isFetching)} />

        <InfoCard title="Success" value={String(current.isSuccess)} />

        <InfoCard title="Error" value={String(current.isError)} />
      </div>

      {current.error && (
        <div className="rounded-lg border border-red-500 bg-red-50 p-4">
          <h2 className="mb-2 text-lg font-semibold text-red-600">Error</h2>

          <pre className="overflow-auto text-sm">
            {JSON.stringify(current.error, null, 2)}
          </pre>
        </div>
      )}

      <div className="rounded-lg border">
        <div className="border-b p-4">
          <h2 className="text-xl font-semibold">Response JSON</h2>
        </div>

        <pre className="max-h-[70vh] overflow-auto bg-gray-950 p-6 text-sm text-green-400">
          {JSON.stringify(current.data, null, 2)}
        </pre>
      </div>
    </div>
  );
}

type InfoCardProps = {
  title: string;
  value: string;
};

function InfoCard({ title, value }: InfoCardProps) {
  return (
    <div className="rounded-lg border p-4">
      <div className="text-sm text-gray-500">{title}</div>

      <div className="mt-1 text-lg font-semibold">{value}</div>
    </div>
  );
}
